<script setup lang="ts">
import { type Component, computed } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import type { IconOptions, VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';
import SuccessIcon from '~icons/ion/checkmark-circle-outline';
import ErrorIcon from '~icons/ion/close-circle-outline';
import InfoIcon from '~icons/ion/information-circle-outline';
import WarningIcon from '~icons/ion/warning-outline';

export type KaMessageVariant = 'default' | 'info' | 'success' | 'warning' | 'error';

/** Controls whether and how the component announces itself to screen readers.
 *  - `"off"` — no live region; use for static, always-rendered notifications.
 *  - `"polite"` — `role="status"` (waits its turn); use for async status updates.
 *  - `"assertive"` — `role="alert"` (interrupts); use for time-sensitive errors.
 *  When omitted the component falls back to variant-based defaults (error →
 *  assertive, all others → polite). WCAG 2.2 SC 4.1.3.
 */
export type KaMessageLive = 'off' | 'polite' | 'assertive';

export interface KaMessageProps {
  message?: string;
  background?: 'solid' | 'transparent' | 'none' | 'notification';
  variant?: KaMessageVariant;
  left?: IconOptions;
  hideLeftIcon?: boolean;
  class?: VueClass;
  /**
   * Semantic live-region control. Prefer this over `role` for new consumers.
   * See `KaMessageLive` for the three options.
   */
  live?: KaMessageLive;
  /**
   * Low-level ARIA role override. Pass `null` to suppress the live-region role
   * while keeping `aria-live="polite"` — use this when the error text is
   * already associated with a control via `aria-errormessage`/`aria-invalid`
   * so axe-core's aria-errormessage rule is satisfied. WCAG 2.2 SC 4.1.3.
   */
  role?: string | null;
}

const props = withDefaults(defineProps<KaMessageProps>(), {
  variant: 'default',
  background: 'transparent',
  hideLeftIcon: false,
  class: '',
});

// Priority: explicit role prop → live prop → variant-based fallback.
// role=null (K10-36213): suppresses role but keeps aria-live="polite".
// live="off": no role and no aria-live — for static, always-rendered banners.
// live="polite"/"assertive": maps to role="status"/"alert" regardless of variant.
// Fallback: error → role="alert" (assertive), all others → role="status" (polite).
const resolvedRole = computed(() => {
  if (props.role !== undefined) {
    if (props.role === null) return undefined;
    return props.role;
  }
  if (props.live !== undefined) {
    if (props.live === 'off') return undefined;
    return props.live === 'assertive' ? 'alert' : 'status';
  }
  return props.variant === 'error' ? 'alert' : 'status';
});

// Only emit aria-live when the role is suppressed via role=null so the element
// still satisfies the aria-errormessage requirement in axe-core (K10-36213).
// live="off" intentionally emits nothing — no announcements at all.
const resolvedAriaLive = computed(() => (props.role === null ? 'polite' : undefined));

const { dataTestid, suffix } = useDataTestid();

const isDefault = computed(() => props.variant === 'default');
const isInfo = computed(() => props.variant === 'info');
const isSuccess = computed(() => props.variant === 'success');
const isWarning = computed(() => props.variant === 'warning');
const isError = computed(() => props.variant === 'error');

const isSolid = computed(() => props.background === 'solid');
const isNoBg = computed(() => props.background === 'none');
const isNotification = computed(() => props.background === 'notification');

const defaultIcons: Record<KaMessageVariant, Component> = {
  default: InfoIcon,
  info: InfoIcon,
  success: SuccessIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};
</script>
<template>
  <div
    v-if="props.message || $slots.message"
    :data-testid="dataTestid"
    :role="resolvedRole"
    :aria-live="resolvedAriaLive"
    :class="
      cnMerge(
        'flex items-start gap-1.5 rounded px-2 py-1.5 text-xs font-normal ring-1 ring-inset',

        isDefault &&
          'bg-gray-50 text-gray-700 ring-gray-500/20 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-300/30',
        isDefault && isSolid && 'bg-gray-600 dark:bg-gray-400',
        isDefault && isNotification && 'border-gray-500 bg-gray-50 dark:bg-gray-400/10',

        isInfo &&
          'bg-blue-50 text-blue-700 ring-blue-500/20 dark:bg-blue-400/10 dark:text-blue-200 dark:ring-blue-300/30 [&_a]:text-blue-700 [&_a]:hover:text-blue-800 dark:[&_a]:text-blue-200 dark:[&_a]:hover:text-blue-100',
        isInfo && isSolid && 'bg-blue-600 dark:bg-blue-400',
        isInfo && isNotification && 'border-blue-500 bg-blue-50 dark:bg-blue-400/10',

        isSuccess &&
          'bg-green-50 text-green-800 ring-green-500/20 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/30',
        isSuccess && isSolid && 'bg-green-600 dark:bg-green-500',
        isSuccess &&
          isNotification &&
          'border-green-500 bg-green-50 dark:bg-green-400/10 dark:text-green-50',

        isWarning &&
          'bg-orange-50 text-orange-800 ring-orange-500/20 dark:bg-orange-400/10 dark:text-orange-400 dark:ring-orange-400/30',
        isWarning && isSolid && 'bg-orange-600 dark:bg-orange-500',
        isWarning &&
          isNotification &&
          'border-orange-500 bg-orange-50 dark:bg-orange-400/10 dark:text-orange-50',

        isError &&
          'bg-red-50 text-red-700 ring-red-500/20 dark:bg-red-400/5 dark:text-red-400 dark:ring-red-400/30',
        isError && isSolid && 'bg-red-600 dark:bg-red-500',
        isError && isNotification && 'border-red-500 bg-red-50 dark:bg-red-400/10 dark:text-red-50',

        isSolid && 'text-white dark:text-black',

        isNotification &&
          'items-center gap-3 rounded border-l-4 p-4 text-sm text-gray-900 ring-0 dark:text-gray-50',

        isNoBg && 'bg-transparent p-0 ring-0 dark:bg-transparent',

        props.class,
      )
    "
  >
    <span
      v-if="!props.hideLeftIcon"
      :id="$attrs.id ? `${$attrs.id}-icon` : undefined"
      :data-testid="suffix('icon')"
      :class="['flex flex-shrink-0 self-start', isNotification ? 'size-5' : 'size-4']"
      aria-hidden="true"
    >
      <slot name="left">
        <component
          :is="!!props.left ? props.left.icon : defaultIcons[props.variant]"
          :class="
            cnMerge(
              'h-full w-full outline-none',
              isDefault && 'text-gray-600 dark:text-gray-300',
              isError && 'text-red-600 dark:text-red-400',
              isWarning && 'text-orange-600 dark:text-orange-400',
              isSuccess && 'text-green-600 dark:text-green-400',
              isInfo && 'text-blue-600 dark:text-blue-200',
              isSolid && 'text-white dark:text-black',
              props.left?.classes,
            )
          "
          tabindex="-1"
        />
      </slot>
    </span>

    <slot name="message">
      <p
        :id="$attrs.id ? `${$attrs.id}-message` : undefined"
        :data-testid="suffix('message')"
        class="m-0 p-0"
      >
        {{ message }}
      </p>
    </slot>
  </div>
</template>
